import { type ReactNode } from "react";
import { FiHelpCircle } from "react-icons/fi";
import Tooltip from "components/Tooltip";
import { cn } from "utils/helpers";

const SIZE_MAP = {
	sm: 18,
	md: 22,
	lg: 26,
} as const;

export type HelpIconSize = keyof typeof SIZE_MAP;

export interface HelpIconProps {
	/** Optional className applied to the trigger button. */
	className?: string;
	/** Test id for the trigger button. */
	"data-testid"?: string;
	/** Disable the trigger (e.g. no steps available). */
	disabled?: boolean;
	/** Icon fill colour. */
	fill?: string;
	/** Accessible label / tooltip. */
	label?: string;
	/** Optional sibling content rendered before the icon (e.g. a heading). */
	parent?: ReactNode;
	/** Icon size: `"sm" | "md" | "lg"` or an explicit pixel number. */
	size?: HelpIconSize | number;
	/** Icon stroke colour. */
	stroke?: string;
	/** Click handler — typically `start` from `useGuidedTour`. */
	onClick: () => void;
}

const DEFAULT_STROKE = "#0031DD";

/**
 * HelpIcon — pure-presentation help trigger.
 *
 * Renders an `<FiHelpCircle>` button wrapped in a `Tooltip`. Stateless; the
 * caller wires `onClick` to a tour starter (typically `useGuidedTour`).
 *
 * Pass `parent` to render content (e.g. a heading) immediately before the
 * icon inside a flex row — useful for adding contextual help next to a
 * page title without changing the surrounding layout.
 */
export const HelpIcon: React.FC<HelpIconProps> = ({
	onClick,
	disabled = false,
	size = "sm",
	stroke = DEFAULT_STROKE,
	fill = "none",
	label = "Show me how this page works",
	className,
	parent,
	"data-testid": dataTestId = "help-trigger",
}) => {
	const pixelSize = typeof size === "number" ? size : SIZE_MAP[size];

	const button = (
		<Tooltip label={label}>
			<button
				type="button"
				onClick={onClick}
				disabled={disabled}
				className={cn(
					"inline-flex items-center justify-center rounded-full transition-colors hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-[#0031DD]/40 disabled:cursor-not-allowed disabled:opacity-50",
					className,
				)}
				data-testid={dataTestId}
			>
				<FiHelpCircle
					size={pixelSize}
					stroke={stroke}
					fill={fill}
					aria-hidden="true"
				/>
			</button>
		</Tooltip>
	);

	if (!parent) {
		return button;
	}

	return (
		<div className="flex items-center gap-2">
			{parent}
			{button}
		</div>
	);
};
